import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import ChangePassword from "@/pages/ChangePassword";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Terms from "@/pages/Terms";
import PatientHome from "@/pages/patient/PatientHome";
import DoctorProfile from "@/pages/patient/DoctorProfile";
import MyAppointments from "@/pages/patient/MyAppointments";
import PatientProfile from "@/pages/patient/PatientProfile";
import DoctorLayout from "@/layouts/DoctorLayout";
import Dashboard from "@/pages/doctor/Dashboard";
import Availability from "@/pages/doctor/Availability";
import Patients from "@/pages/doctor/Patients";
import Assistants from "@/pages/doctor/Assistants";
import DoctorProfileEdit from "@/pages/doctor/DoctorProfileEdit";
import Consultation from "@/pages/doctor/Consultation";
import AdminPanel from "@/pages/admin/AdminPanel";
import PublicBooking from "@/pages/PublicBooking";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-400 font-semibold">
      Cargando…
    </div>
  );
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/cambiar-clave" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Auth mode="login" />} />
      <Route path="/registro" element={<Auth mode="register" />} />
      <Route path="/cambiar-clave" element={<ChangePassword />} />
      <Route path="/olvide-clave" element={<ForgotPassword />} />
      <Route path="/restablecer" element={<ResetPassword />} />
      <Route path="/terminos" element={<Terms />} />
      <Route path="/agendar/:doctorId" element={<PublicBooking />} />

      <Route
        path="/buscar"
        element={
          <Protected roles={["patient"]}>
            <PatientHome />
          </Protected>
        }
      />
      <Route
        path="/medico/:doctorId"
        element={
          <Protected roles={["patient"]}>
            <DoctorProfile />
          </Protected>
        }
      />
      <Route
        path="/mis-citas"
        element={
          <Protected roles={["patient"]}>
            <MyAppointments />
          </Protected>
        }
      />
      <Route
        path="/mi-perfil"
        element={
          <Protected roles={["patient"]}>
            <PatientProfile />
          </Protected>
        }
      />

      <Route
        element={
          <Protected roles={["doctor", "assistant"]}>
            <DoctorLayout />
          </Protected>
        }
      >
        <Route path="/panel" element={<Dashboard />} />
        <Route path="/panel/disponibilidad" element={<Protected roles={["doctor"]}><Availability /></Protected>} />
        <Route path="/panel/pacientes" element={<Protected roles={["doctor"]}><Patients /></Protected>} />
        <Route path="/panel/asistentes" element={<Protected roles={["doctor"]}><Assistants /></Protected>} />
        <Route path="/panel/perfil" element={<Protected roles={["doctor"]}><DoctorProfileEdit /></Protected>} />
        <Route path="/panel/consulta/:appointmentId" element={<Protected roles={["doctor"]}><Consultation /></Protected>} />
      </Route>

      <Route
        path="/admin"
        element={
          <Protected roles={["admin"]}>
            <AdminPanel />
          </Protected>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
